﻿import React, { useState, useEffect } from 'react';
import { CrudFunctions } from '../utilities/crud-functions';
import { AssetData } from '../models/asset-data';
import { FieldUpdate } from '../models/field-update';
import { GasChromatographyInstrumentLookupData } from '../models/gas-chromatography-instrument-lookup-data';
import { GasChromatographyAsset } from '../models/gas-chromatography-asset';
import { DataTemplates } from '../templates/data-templates';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import {
    TextBox,
    SelectBox,
    NumberBox,
    CheckBox
} from 'devextreme-react';

export interface Props {
    isReadOnly: boolean;
    assetId?: string;
    assetData?: AssetData;
    onGcInstrumentChanged?: (instrument: GasChromatographyAsset) => void;
}

export default function GcAssetComponent(props: Props) {
    const [gasChromatographyAsset, setGasChromatographyAsset] =
        useState<GasChromatographyAsset>();

    const [gasChromatographyInstrumentLookupData, setGasChromatographyInstrumentLookupData] =
        useState<GasChromatographyInstrumentLookupData>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    //const [dataLoaded, setDataLoaded] =
    //  useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.assetId]);

    useEffect(() => {
        if (props.onGcInstrumentChanged) {
            console.log("calling parent's onGcInstrumentChanged..")
            props.onGcInstrumentChanged(gasChromatographyAsset);
        }
    }, [gasChromatographyAsset]);

    return (
        <div>
            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-15">
                    <div className="pw-form-unit-label">Front Inlet</div>
                    <SelectBox id="frontInletId" readOnly={props.isReadOnly}
                        placeholder="na"
                        showClearButton={true}
                        itemRender={DataTemplates.LookupItemNameOnlyTemplate}
                        dataSource={gasChromatographyInstrumentLookupData &&
                            gasChromatographyInstrumentLookupData.gcInlets ?
                            gasChromatographyInstrumentLookupData.gcInlets : []}
                        valueExpr='id' displayExpr="name"
                        onValueChanged={onGasChromatographyValueChanged}
                        value={gasChromatographyAsset &&
                            gasChromatographyAsset.frontInletId ?
                            gasChromatographyAsset.frontInletId : undefined} />
                </div>

                <div className="pw-form-unit pw-comp-15">
                    <div className="pw-form-unit-label">Back Inlet</div>
                    <SelectBox id="backInletId" readOnly={props.isReadOnly}
                        placeholder="na"
                        showClearButton={true}
                        itemRender={DataTemplates.LookupItemNameOnlyTemplate}
                        dataSource={gasChromatographyInstrumentLookupData &&
                            gasChromatographyInstrumentLookupData.gcInlets ?
                            gasChromatographyInstrumentLookupData.gcInlets : []}
                        valueExpr='id' displayExpr="name"
                        onValueChanged={onGasChromatographyValueChanged}
                        value={gasChromatographyAsset &&
                            gasChromatographyAsset.backInletId ?
                            gasChromatographyAsset.backInletId : undefined} />
                </div>
            </div>

            <div className="pw-comp-row pw-margin-top-05">
                <div className="pw-form-unit pw-comp-15">
                    <div className="pw-form-unit-label">Front Detector</div>
                    <SelectBox id="frontDetectorId" placeholder="na" readOnly={props.isReadOnly}
                        showClearButton={true}
                        itemRender={DataTemplates.LookupItemNameOnlyTemplate}
                        dataSource={gasChromatographyInstrumentLookupData &&
                            gasChromatographyInstrumentLookupData.gcDetectors ?
                            gasChromatographyInstrumentLookupData.gcDetectors : []}
                        valueExpr='id' displayExpr="name"
                        onValueChanged={onGasChromatographyValueChanged}
                        value={gasChromatographyAsset &&
                            gasChromatographyAsset.frontDetectorId ?
                            gasChromatographyAsset.frontDetectorId : undefined} />
                </div>

                <div className="pw-form-unit pw-comp-15">
                    <div className="pw-form-unit-label">Back Detector</div>
                    <SelectBox id="backDetectorId" placeholder="na" readOnly={props.isReadOnly}
                        showClearButton={true}
                        itemRender={DataTemplates.LookupItemNameOnlyTemplate}
                        dataSource={gasChromatographyInstrumentLookupData &&
                            gasChromatographyInstrumentLookupData.gcDetectors ?
                            gasChromatographyInstrumentLookupData.gcDetectors : []}
                        valueExpr='id' displayExpr="name"
                        onValueChanged={onGasChromatographyValueChanged}
                        value={gasChromatographyAsset &&
                            gasChromatographyAsset.backDetectorId ?
                            gasChromatographyAsset.backDetectorId : undefined} />
                </div>
            </div>

            <div className="pw-comp-row pw-margin-top-05">
                <div className="pw-form-unit pw-comp-15">
                    <div className="pw-form-unit-label">Other Detector</div>
                    <TextBox id="otherDetector" className="textbox" readOnly={props.isReadOnly}
                        onValueChanged={onGasChromatographyValueChanged}
                        showClearButton={true}
                        placeholder="na"
                        value={gasChromatographyAsset ?
                            gasChromatographyAsset.otherDetector : undefined} />
                </div>

                <div className="pw-form-unit pw-comp-15">
                    <CheckBox id="massSpectrometerDetector" className="textbox in-row-checkbox"
                        onValueChanged={onGasChromatographyValueChanged} readOnly={props.isReadOnly}
                        text="Mass Spectrometer Detector"
                        value={gasChromatographyAsset ? gasChromatographyAsset.massSpectrometerDetector : false} />
                </div>
            </div>

            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-15">
                    <div className="pw-form-unit-label">Carrier Gas</div>
                    <SelectBox id="carrierGasId"
                        readOnly={props.isReadOnly}
                        placeholder="na"
                        showClearButton={true}
                        itemRender={DataTemplates.LookupItemTitleDescriptionRender}
                        dataSource={gasChromatographyInstrumentLookupData?.gcCarrierGases ?? []}
                        valueExpr='id' displayExpr="name"
                        value={gasChromatographyAsset?.carrierGas?.id ?? undefined}
                        onValueChanged={onCarrierGasChanged}
                    />
                </div>
            </div>

            {
                globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }
        </div>
    );

    function onGasChromatographyValueChanged(e: any) {
        console.log("onGasChromatographyValueChanged:");
        console.log(e);

        // TODO need to do more testing here 
        //if (e.element && e.element.id && gasChromatographyAsset) {
        if (e.element && e.element.id) {
            const caller = e.element.id;

/*            let fieldUpdate: FieldUpdate = { entity: "GasChromatographyAssets", field: caller, value: e.value }*/

            // todo 
            //if (gasChromatographyAsset) {
            e.rowId = gasChromatographyAsset.assetId;

            // Setting this state triggers side effect -> call the parent's onGcInstrumentChanged handler
            setGasChromatographyAsset({ ...gasChromatographyAsset, [caller]: e.value });

            // todo delete? 
            /*
            this.setState((prevState: any) => ({
                gasChromatographyAsset: {
                    ...prevState.gasChromatographyAsset,
                    [e.field]: e.value
                }
            }));
            */

            // this is so the field name (such as 'frontInletId') gets modified
            // so it will resolve on the backend, for the Analysis.GasChromatographyAssets table,
            // but it has to be the correct name for the denormalized field ('frontInletId') of the model (above)
            // to set local state properly

            //e.field = e.field.replace("Id", "");


            // updateAssetField(e);
            //}
        }
    }

    function onCarrierGasChanged(e: any) {
        console.log("OnCarrierGasChanged: ", e);
        if (e.value && e.event && gasChromatographyInstrumentLookupData?.gcCarrierGases && gasChromatographyAsset) {
            const matchingItem = gasChromatographyInstrumentLookupData.gcCarrierGases.find(i => i.id === e.value);
            if (matchingItem) {
                setGasChromatographyAsset({ ...gasChromatographyAsset, carrierGas: matchingItem, carrierGasId: e.value });
            }
        }
    }

    async function getGcInstrumentEnumerations() {
        await CrudFunctions.getData("benchsheets/gcinstrumentlookupdata")
            .then(data => {
                setGasChromatographyInstrumentLookupData(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Enumerations");
            });
    }

    async function getGcAsset() {
        try {
            const resp = await CrudFunctions.getData(`benchsheets/gcinstrument/${props.assetId}`);
            setGasChromatographyAsset(resp)
        }
        catch (err) {
            // Database did not find a gc record
            // need to initialize a default so user can update the fields
            setGasChromatographyAsset({
                assetId: props.assetId,
                //checktunePassed: false, // TODO field should not exist? 
                massSpectrometerDetector: false
            });
            //showMessage(err, ModalMessageType.Error, "Error Retrieving GC Instrument");
        }
    }

    async function getAllData() {
        if (!props.assetId)
            return;

        await Promise.all([
            getGcInstrumentEnumerations(),
            getGcAsset()
        ]);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }


}
